import { MapUtils } from '../../utility/map-utils.js';
import { MigrationSafe } from '../core/migration-safe.js';
import { VersionRange } from '../core/version-range.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';

export class BackstageTicket extends DataModel {
  static readonly TYPE_ID: string = '91c701353ab94f018968d3f83c2cf96f';

  static readonly SCHEMA_VERSION = 2;

  private constructor(
    public readonly name: string,
    public readonly productId: number,
    public readonly priceId: number,
    public readonly grossPrice: number,
    public readonly available: MigrationSafe<boolean>,
  ) {
    super();
  }

  get $typeId(): string {
    return BackstageTicket.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BackstageTicket.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static create(args: {
    name: string,
    productId: number,
    priceId: number,
    grossPrice: number,
    available: MigrationSafe<boolean>,
  }): BackstageTicket {
    return new BackstageTicket(
        args.name,
        args.productId,
        args.priceId,
        args.grossPrice,
        args.available,
    );
  }

  static fromData(
      context: SerializationContext,
      map: SerializedData,
  ): BackstageTicket {
    return new BackstageTicket(
        MapUtils.getString(map, 'name'),
        MapUtils.getNumber(map, 'productId'),
        MapUtils.getNumber(map, 'priceId'),
        MapUtils.getNumber(map, 'grossPrice'),
        MigrationSafe.decode<boolean, boolean>(
            MapUtils.getRaw(map, 'available'),
            (value) => value,
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      name: this.name,
      productId: this.productId,
      priceId: this.priceId,
      grossPrice: this.grossPrice,
      available: this.available.encode((value) => value),
    };
  }

  clone(): this {
    return new BackstageTicket(
        this.name,
        this.productId,
        this.priceId,
        this.grossPrice,
        this.available.clone((value) => value),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.name === this.name &&
      other.productId === this.productId &&
      other.priceId === this.priceId &&
      other.grossPrice === this.grossPrice &&
      other.available.isSame(this.available);
  }
}

export class BackstageTicketBuilder extends DataModelBuilder<BackstageTicket> {
  build(context: SerializationContext, map: SerializedData): BackstageTicket {
    return BackstageTicket.fromData(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BackstageTicket.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(BackstageTicket.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - Add `available` field of type {@link MigrationSafe<boolean>}.
     */
    if(version < 2) {
      map['available'] = MigrationSafe.UNDEFINED_STATUS;

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
